import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface IDirection {
  isRTL: boolean;
  toggleLayoutDirection: (value: boolean) => void;
}

const LayoutDirectionContext = createContext<IDirection>({
  isRTL: false,
  toggleLayoutDirection: () => {},
});

export const useLayoutDirection = () => useContext(LayoutDirectionContext);

const getLangFromUrl = (): { isHe: boolean } | null => {
  const idx = window.location.search.indexOf('lang=');
  if (idx < 0) return null;
  return { isHe: window.location.search.indexOf('lang=he') >= 0 };
};

export const LayoutDirectionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  const [isRTL, setIsRTL] = useState<boolean>(() => {
    const fromUrl = getLangFromUrl();

    if (fromUrl) {
      localStorage.setItem('isRTL', fromUrl.isHe ? 'true' : 'false');
      i18n.changeLanguage(fromUrl.isHe ? 'he' : 'en');
      return fromUrl.isHe;
    }

    const stored = localStorage.getItem('isRTL');
    if (stored !== null) {
      const _isRTL = stored === 'true';
      i18n.changeLanguage(_isRTL ? 'he' : 'en');
      return _isRTL;
    }

    const _isRTL = i18n.language === 'he';
    localStorage.setItem('isRTL', _isRTL ? 'true' : 'false');
    return _isRTL;
  });

  useEffect(() => {
    localStorage.setItem('isRTL', isRTL.toString());

    const rootHtml = document.getElementById('root-html');
    rootHtml?.setAttribute('dir', isRTL ? 'rtl' : 'ltr');

    document.documentElement.setAttribute('lang', isRTL ? 'he' : 'en');
    i18n.changeLanguage(isRTL ? 'he' : 'en');
  }, [isRTL]);

  return (
    <LayoutDirectionContext.Provider value={{ isRTL, toggleLayoutDirection: setIsRTL }}>
      {children}
    </LayoutDirectionContext.Provider>
  );
};
